import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import Cookies from "js-cookie";
import { adyenMerchantId, collectionEmail } from "src/config/app-config";
import { appStore } from "src/stores/app/app.store";
import { useStoreSettings } from "src/hooks/useStoreSettings";
import { useCartData } from "src/hooks/stores/use-cart-store";
import { lineItemsTotalPrice } from "src/utils/ct/totalPrice";
import { getCartItemAvailability } from "src/utils/ct/cartItemAvailabilityHelper";
import { COOKIE_NAME } from "src/molecules/ConsentMangerBanner";
import type { FeatureFlags } from "src/types/featureFlags";
import type { LineItem, Money } from "src/types/ctgraphql.d";

type ParsedCookie = {
  custom?: Record<string, boolean>;
};

type AppBootstrapProps = {
  featureFlags?: FeatureFlags;
};

function computeCartMinDiff(
  lineItems: LineItem[] | undefined,
  minCartSubtotal: number | null
): Money | null {
  const inStockLineItems = lineItems?.filter(
    (lineItem) => !getCartItemAvailability(lineItem).isItemOutOfStock
  );

  if (!inStockLineItems?.length) {
    return null;
  }

  const cartLineItemsTotalPrice = lineItemsTotalPrice(inStockLineItems);

  if (!cartLineItemsTotalPrice.centAmount || !minCartSubtotal) {
    return null;
  }

  const diff = minCartSubtotal - cartLineItemsTotalPrice.centAmount;

  return {
    ...cartLineItemsTotalPrice,
    centAmount: diff > 0 ? diff : 0,
  };
}

export function AppBootstrap({ featureFlags }: AppBootstrapProps): null {
  const router = useRouter();
  const { data: storeSettingsData, refetch: refetchStoreSettings } =
    useStoreSettings();
  const { cart } = useCartData();
  const consentSeededRef = useRef(false);

  const minCartSubtotal =
    storeSettingsData?.storeSettings?.min_cart_subtotal_price_cent_amount ??
    null;

  useEffect(() => {
    appStore.setAdyenMerchantId(adyenMerchantId);
  }, []);

  useEffect(() => {
    appStore.setCollection({ email: collectionEmail });
  }, []);

  useEffect(() => {
    if (featureFlags) {
      appStore.setFeatureFlags(featureFlags);
    }
  }, [featureFlags]);

  useEffect(() => {
    appStore.setCartMinLineItemsTotalPriceCentAmount(minCartSubtotal);
  }, [minCartSubtotal]);

  useEffect(() => {
    appStore.setRefetchStoreSettings(refetchStoreSettings);
  }, [refetchStoreSettings]);

  useEffect(() => {
    appStore.setCartMinLineItemsTotalPriceDiff(
      computeCartMinDiff(cart?.lineItems, minCartSubtotal)
    );
  }, [cart?.lineItems, minCartSubtotal]);

  useEffect(() => {
    appStore.setShouldScrollToFooter(router.query.section === "opt-in");
  }, [router.query.section]);

  // Seed consent state once from the cookie (matches the previous _app init).
  useEffect(() => {
    if (consentSeededRef.current) {
      return;
    }
    consentSeededRef.current = true;

    const cookie = Cookies.get(COOKIE_NAME);
    appStore.setDisplayConsentManager(!cookie);

    if (!cookie) {
      return;
    }

    try {
      const parsedCookie: ParsedCookie = JSON.parse(cookie);
      if (parsedCookie.custom && typeof parsedCookie.custom === "object") {
        if (Object.values(parsedCookie.custom).includes(true)) {
          appStore.setInitializedConsent(true);
        }
      }
    } catch (error) {
      console.error("Failed to parse cookie", error);
    }
  }, []);

  return null;
}
